import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  buildCustomDateDisplayFormat,
  DATE_DISPLAY_FORMAT_OPTIONS,
  DATE_FORMAT_PATTERN_PRESETS,
  DATE_FORMAT_TOKEN_HELP,
  DEFAULT_CUSTOM_DATE_FORMAT_PATTERN,
  formatDateTimeDisplay,
  getCustomDateDisplayPattern,
  getCustomPatternBucketWarning,
  normalizeDateDisplayFormatSelectValue,
} from '../../utils/dateBucketFormat';

export default function DateDisplayFormatControl({
  format,
  onChange,
  previewValue = '2026-07-21',
  dateBucket,
  size = 'small',
  selectLabel = 'Formato de visualización',
  helperText = 'Solo afecta cómo se muestra en tabla y ejes. No cambia la consulta SQL.',
  showHelper = true,
}) {
  const selectValue = normalizeDateDisplayFormatSelectValue(format);
  const pattern = getCustomDateDisplayPattern(format);

  const preview = useMemo(() => {
    if (selectValue !== 'custom' || !pattern.trim()) return null;
    return formatDateTimeDisplay(previewValue, {
      dateDisplayFormat: buildCustomDateDisplayFormat(pattern),
      dateBucket,
    });
  }, [selectValue, pattern, previewValue, dateBucket]);

  const bucketWarning = useMemo(
    () => (selectValue === 'custom' ? getCustomPatternBucketWarning(pattern, dateBucket) : null),
    [selectValue, pattern, dateBucket]
  );

  const handleSelectChange = (next) => {
    if (next === 'auto') {
      onChange(undefined);
      return;
    }
    if (next === 'custom') {
      onChange(buildCustomDateDisplayFormat(pattern || DEFAULT_CUSTOM_DATE_FORMAT_PATTERN));
      return;
    }
    onChange(next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FormControl fullWidth size={size}>
        <InputLabel>{selectLabel}</InputLabel>
        <Select
          label={selectLabel}
          value={selectValue}
          onChange={(e) => handleSelectChange(e.target.value)}
        >
          {DATE_DISPLAY_FORMAT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectValue === 'custom' && (
        <>
          <TextField
            size={size}
            fullWidth
            label="Plantilla"
            placeholder={DEFAULT_CUSTOM_DATE_FORMAT_PATTERN}
            value={pattern}
            onChange={(e) => onChange(buildCustomDateDisplayFormat(e.target.value))}
            helperText={DATE_FORMAT_TOKEN_HELP}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {DATE_FORMAT_PATTERN_PRESETS.map((preset) => (
              <Chip
                key={preset.pattern}
                size="small"
                label={preset.label}
                variant={pattern === preset.pattern ? 'filled' : 'outlined'}
                onClick={() => onChange(buildCustomDateDisplayFormat(preset.pattern))}
              />
            ))}
          </Box>
          {preview && (
            <Typography variant="caption" color="text.secondary">
              Vista previa: {preview}
            </Typography>
          )}
          {bucketWarning && (
            <Typography variant="caption" color="warning.main">
              {bucketWarning}
            </Typography>
          )}
        </>
      )}

      {showHelper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
